import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { ADMIN_RUNTIME_COMMANDS } from '../../../app/runtime/adminRuntime';
import { useShellAppContext } from '../../shell/context/useShellAppContext';
import { IndexingStatus } from '../operatorState';
import { normalizeCatalogSourcePhase } from '../../catalog-sync/catalogSourcePhase';
import {
  notifyIndexingRequested,
  notifyIndexingRequestFailed,
  notifyReindexRequired,
  subscribeToIndexingRequested,
  subscribeToIndexingRequestFailed,
  subscribeToReindexRequired,
} from '../../operation-supervisor/indexingRequestEvents';
import { useLocalizedMessage } from '../../../util/useLocalizedMessage';

// ---------------------------------------------------------------------------
// Poll interval strategy
// ---------------------------------------------------------------------------
//   running / queued : 1 s  — live progress
//   error            : 5 s  — retry window
//   idle / ready     : 10 s — background refresh
//   hidden tab       : 15 s — no heavy polling when not visible
// ---------------------------------------------------------------------------
const INTERVAL_ACTIVE_MS = 1000;
const INTERVAL_ERROR_MS = 5000;
const INTERVAL_IDLE_MS = 10000;
const INTERVAL_HIDDEN_MS = 15000;
const OPTIMISTIC_REQUEST_POLL_DELAY_MS = 300;
const OPTIMISTIC_REQUEST_HOLD_MS = 75000;
function getInterval(status) {
  if (document.visibilityState !== 'visible') return INTERVAL_HIDDEN_MS;
  if (status === IndexingStatus.RUNNING) return INTERVAL_ACTIVE_MS;
  if (status === IndexingStatus.ERROR) return INTERVAL_ERROR_MS;
  return INTERVAL_IDLE_MS;
}

function normalizeStatus(raw) {
  if (raw === 'done' || raw === 'completed') return IndexingStatus.READY;
  if (raw === 'running' || raw === 'queued') return IndexingStatus.RUNNING;
  if (raw === 'error' || raw === 'failed') return IndexingStatus.ERROR;
  return IndexingStatus.IDLE;
}

function pickTimestamp(...candidates) {
  for (let i = 0; i < candidates.length; i += 1) {
    const value = candidates[i];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    } else if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
      // Accept both unix seconds and unix milliseconds.
      const ms = value < 1e12 ? value * 1000 : value;
      return new Date(ms).toISOString();
    } else if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.toISOString();
    }
  }
  return null;
}

function parseState(response) {
  // A readable live index does not mean its replacement job has finished.
  // Preserve queue state so polling can dispatch the rebuild's worker.
  const canSearch = response?.state?.readiness?.can_search === true;
  const idx = response?.state?.indexing ?? response?.current ?? null;
  const rawStatus = String(idx?.status ?? 'idle');
  const jobStatus = normalizeStatus(rawStatus);
  const indexedCount = typeof (idx?.indexed_count ?? idx?.indexedCount) === 'number'
    ? (idx.indexed_count ?? idx.indexedCount)
    : 0;
  const totalCount = typeof (idx?.total_count ?? idx?.totalCount) === 'number'
    ? (idx.total_count ?? idx.totalCount)
    : 0;
  const explicitProgress = typeof idx?.progress === 'number' ? idx.progress : null;
  const progressKnown = totalCount > 0 || (explicitProgress !== null && explicitProgress > 0);
  const calculatedProgress = totalCount > 0
    ? (indexedCount / totalCount) * 100
    : 0;
  return {
    ...normalizeCatalogSourcePhase({ ...idx, status: rawStatus }),
    rawStatus,
    status: jobStatus === IndexingStatus.IDLE && canSearch ? IndexingStatus.READY : jobStatus,
    progress: Math.max(0, Math.min(100, explicitProgress ?? calculatedProgress)),
    progressKnown,
    activeIndexPresent: canSearch
      || idx?.active_index_present === true
      || Boolean(response?.lastSuccessAt || response?.lastFull?.successAt),
    error: idx?.error ? String(idx.error) : null,
    lastSuccessAt: pickTimestamp(
      idx?.last_success_at,
      idx?.finished_at,
      idx?.updated_at,
      idx?.last_successful_at,
      idx?.last_successful_update_at,
      idx?.last_completed_at,
      response?.lastSuccessAt,
      response?.lastFull?.successAt,
    ),
    startedAt: idx?.started_at ?? idx?.startedAt ?? null,
    finishedAt: idx?.finished_at ?? idx?.finishedAt ?? null,
    jobId: idx?.job_id ?? idx?.jobId ?? idx?.taskId ?? null,
    queueAgeSeconds: typeof idx?.queue_age_seconds === 'number' ? idx.queue_age_seconds : null,
    isStaleQueue: idx?.is_stale_queue === true,
    restartAllowed: idx?.restart_allowed === true,
    indexedCount,
    totalCount,
    reindexRequired: response?.state?.reindexRequired === true,
  };
}

/**
 * useCatalogSync — adaptive-polling hook for the CatalogSyncControlCard.
 *
 * Polling cadence adapts to status so operators see near-real-time progress:
 *   running/queued → 1 s, error → 5 s, idle/ready → 10 s, hidden tab → 15 s.
 *
 * After startFullUpdate() is called the hook immediately sets optimistic
 * "starting" state and forces a poll on the next tick without waiting for
 * the current interval to expire.
 *
 * Returns:
 *   status            — IndexingStatus
 *   progress          — 0-100
 *   activeIndexPresent
 *   error
 *   isLoading
 *   isDegraded        — true when plugin doesn't support onboarding.state
 *   isStarting        — true while indexing.start call is in-flight
 *   lastSuccessAt     — ISO8601 string or null
 *   indexedCount
 *   totalCount
 *   startFullUpdate   — fn, triggers indexing.start + optimistic state
 */
export function useCatalogSync({ enabled = true } = {}) {
  const t = useLocalizedMessage();
  const { runtime, capabilities, compatibility } = useShellAppContext();
  const deferredReindexEnabled = compatibility?.catalogUpdates?.deferred
    ?? capabilities?.deferredReindexV1 === true;
  const shopId = useSelector(state => state.app?.currentShop ?? null);
  const [status, setStatus] = useState(IndexingStatus.IDLE);
  const [rawStatus, setRawStatus] = useState('idle');
  const [progress, setProgress] = useState(0);
  const [progressKnown, setProgressKnown] = useState(false);
  const [activeIndexPresent, setActiveIndexPresent] = useState(false);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDegraded, setIsDegraded] = useState(false);
  const [isStarting, setIsStarting] = useState(false);
  const [lastSuccessAt, setLastSuccessAt] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [finishedAt, setFinishedAt] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [queueAgeSeconds, setQueueAgeSeconds] = useState(null);
  const [isStaleQueue, setIsStaleQueue] = useState(false);
  const [restartAllowed, setRestartAllowed] = useState(false);
  const [indexedCount, setIndexedCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sourcePhase, setSourcePhase] = useState({
    phase: null,
    sourceProcessed: 0,
    sourceTotal: 0,
  });
  const [reindexRequired, setReindexRequired] = useState(false);

  useEffect(() => {
    if (!deferredReindexEnabled) setReindexRequired(false);
  }, [deferredReindexEnabled]);

  const timerRef = useRef(null);
  const statusRef = useRef(IndexingStatus.IDLE); // mutable ref for interval calc
  const cancelledRef = useRef(false);
  const hasValidStateRef = useRef(false); // true once we've parsed a valid poll response
  const autoQueueActionKeyRef = useRef(null); // dedupe kick/restart while queue state is unchanged
  const optimisticRequestRef = useRef(null); // holds external indexing.start race window
  const sendRuntime = useCallback((command, payload = {}) => {
    if (!runtime || !runtime.supports(command)) {
      return Promise.reject(new Error(t('catalog.update.runtimeUnavailable')));
    }
    return runtime.sendMessageWithResponse(command, { shopId, ...payload });
  }, [runtime, shopId, t]);

  // Schedule next poll after `ms` ms. Clears any pending timer first.
  const scheduleNext = useCallback((pollFn, ms) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(pollFn, ms);
  }, []);

  const poll = useCallback(async (scheduleRef) => {
    if (cancelledRef.current || !enabled) return;
    try {
      const response = await sendRuntime(ADMIN_RUNTIME_COMMANDS.INDEXING_STATUS);
      if (cancelledRef.current) return;
      if (response?.result === 'error') {
        if (!hasValidStateRef.current) {
          // Never got a valid response — plugin doesn't support this action
          setIsDegraded(true);
          setIsLoading(false);
          return; // stop polling — degraded mode
        }
        // Had a valid state before — treat as transient error, keep polling
        setError(t('catalog.update.statusReadFailed'));
        setIsLoading(false);
        setIsStarting(false);
        scheduleNext(scheduleRef.current, INTERVAL_ERROR_MS);
        return;
      }

      hasValidStateRef.current = true;
      const parsed = parseState(response);
      const optimisticRequest = optimisticRequestRef.current;
      const optimisticPending = Boolean(
        optimisticRequest
        && Date.now() < optimisticRequest.holdUntil
        && parsed.status !== IndexingStatus.RUNNING
        && parsed.status !== IndexingStatus.ERROR,
      );
      if (!optimisticPending) {
        optimisticRequestRef.current = null;
      }

      const effectiveRawStatus = optimisticPending ? 'queued' : parsed.rawStatus;
      const effectiveStatus = optimisticPending ? IndexingStatus.RUNNING : parsed.status;
      const effectiveProgress = optimisticPending ? 0 : parsed.progress;
      const effectiveProgressKnown = optimisticPending ? false : parsed.progressKnown;

      setRawStatus(effectiveRawStatus);
      setStatus(effectiveStatus);
      setProgress(effectiveProgress);
      setProgressKnown(effectiveProgressKnown);
      setActiveIndexPresent(parsed.activeIndexPresent);
      setError(parsed.error);
      // Keep the last known successful timestamp until a newer explicit
      // success timestamp arrives; running/queued payloads often omit it.
      setLastSuccessAt((previous) => parsed.lastSuccessAt ?? previous);
      setStartedAt(parsed.startedAt);
      setFinishedAt(parsed.finishedAt);
      setJobId(parsed.jobId);
      setQueueAgeSeconds(parsed.queueAgeSeconds);
      setIsStaleQueue(parsed.isStaleQueue);
      setRestartAllowed(parsed.restartAllowed);
      setIndexedCount(parsed.indexedCount);
      setTotalCount(parsed.totalCount);
      setSourcePhase(optimisticPending
        ? { phase: null, sourceProcessed: 0, sourceTotal: 0 }
        : {
          phase: parsed.phase,
          sourceProcessed: parsed.sourceProcessed,
          sourceTotal: parsed.sourceTotal,
        });
      setReindexRequired(deferredReindexEnabled && parsed.reindexRequired);
      setIsLoading(false);
      setIsStarting(optimisticPending);
      statusRef.current = effectiveStatus;

      // Auto-unblock queued jobs so "Queued, waiting for worker" does not stall forever.
      // We dedupe by (jobId + selected action) to avoid spamming bridge on each poll tick.
      if (parsed.rawStatus === 'queued' && parsed.jobId) {
        const shouldRestart = parsed.isStaleQueue && parsed.restartAllowed;
        const action = shouldRestart ? 'index.restart_stale' : 'index.kick';
        const actionKey = `${parsed.jobId}:${action}`;
        if (autoQueueActionKeyRef.current !== actionKey) {
          autoQueueActionKeyRef.current = actionKey;
          const runtimeAction = shouldRestart
            ? ADMIN_RUNTIME_COMMANDS.INDEXING_RESTART_STALE
            : ADMIN_RUNTIME_COMMANDS.INDEXING_KICK;
          if (!runtime.supports(runtimeAction)) {
            autoQueueActionKeyRef.current = actionKey;
          } else sendRuntime(runtimeAction, { job_id: parsed.jobId }).then((resp) => {
            if (!cancelledRef.current && resp?.result === 'error') {
              setError(resp?.error || t('catalog.update.queueActionFailed'));
            } else if (!cancelledRef.current && resp?.meta?.autokick_error) {
              setError(String(resp.meta.autokick_error));
            }
          }).catch((err) => {
            if (!cancelledRef.current) {
              setError(err?.message || t('catalog.update.queueActionFailed'));
            }
          });
        }
      } else {
        // Reset dedupe once queue state is gone (or no job id) so future queues can be kicked again.
        autoQueueActionKeyRef.current = null;
      }

      // Always reschedule (no terminal stop — management surface, status can
      // change from external triggers)
      scheduleNext(scheduleRef.current, getInterval(effectiveStatus));
    } catch (err) {
      if (!cancelledRef.current) {
        setError(err?.message || t('catalog.update.statusReadFailed'));
        setIsLoading(false);
        setIsStarting(false);
        scheduleNext(scheduleRef.current, INTERVAL_ERROR_MS);
      }
    }
  }, [deferredReindexEnabled, enabled, runtime, scheduleNext, sendRuntime, t]);

  const forcePollSoon = useCallback((delay = OPTIMISTIC_REQUEST_POLL_DELAY_MS) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (!cancelledRef.current) {
        const scheduleRef = { current: null };
        const doPoll = () => poll(scheduleRef);
        scheduleRef.current = doPoll;
        doPoll();
      }
    }, delay);
  }, [poll]);

  const applyOptimisticIndexingRequest = useCallback((detail = {}) => {
    if (cancelledRef.current) return;
    const requestedAt = typeof detail.requestedAt === 'number' ? detail.requestedAt : Date.now();
    const holdMs = typeof detail.holdMs === 'number' && detail.holdMs > 0
      ? detail.holdMs
      : OPTIMISTIC_REQUEST_HOLD_MS;

    optimisticRequestRef.current = {
      requestedAt,
      holdUntil: requestedAt + holdMs,
    };

    setIsStarting(true);
    setError(null);
    setRawStatus('queued');
    setStatus(IndexingStatus.RUNNING);
    setProgress(0);
    setProgressKnown(false);
    setSourcePhase({ phase: null, sourceProcessed: 0, sourceTotal: 0 });
    statusRef.current = IndexingStatus.RUNNING;
    forcePollSoon();
  }, [forcePollSoon]);

  const applyIndexingRequestFailure = useCallback((detail = {}) => {
    if (cancelledRef.current) return;
    optimisticRequestRef.current = null;
    setIsStarting(false);
    setRawStatus('error');
    setStatus(IndexingStatus.ERROR);
    setError(detail?.error || t('catalog.update.startFullFailed'));
    statusRef.current = IndexingStatus.ERROR;
  }, [t]);

  const applyReindexRequired = useCallback(() => {
    if (cancelledRef.current || !deferredReindexEnabled) return;
    setReindexRequired(true);
  }, [deferredReindexEnabled]);

  useEffect(() => {
    cancelledRef.current = false;

    if (!enabled) {
      setIsLoading(false);
      setIsDegraded(true);
      return () => {
        cancelledRef.current = true;
        clearTimeout(timerRef.current);
      };
    }

    // Use a ref so the closure inside poll always points to the latest poll fn
    const scheduleRef = { current: null };
    const doPoll = () => poll(scheduleRef);
    scheduleRef.current = doPoll;

    doPoll();

    // Re-calculate interval when tab visibility changes
    const onVisibilityChange = () => {
      if (cancelledRef.current) return;
      scheduleNext(scheduleRef.current, getInterval(statusRef.current));
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelledRef.current = true;
      clearTimeout(timerRef.current);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [enabled, poll, scheduleNext]);

  useEffect(() => {
    const unsubscribeRequested = subscribeToIndexingRequested(applyOptimisticIndexingRequest);
    const unsubscribeFailed = subscribeToIndexingRequestFailed(applyIndexingRequestFailure);
    const unsubscribeRequired = deferredReindexEnabled
      ? subscribeToReindexRequired(applyReindexRequired)
      : () => {};
    return () => {
      unsubscribeRequested();
      unsubscribeFailed();
      unsubscribeRequired();
    };
  }, [
    applyOptimisticIndexingRequest,
    applyIndexingRequestFailure,
    applyReindexRequired,
    deferredReindexEnabled,
  ]);

  const markReindexRequired = useCallback((detail = {}) => {
    if (!deferredReindexEnabled) return Promise.resolve(null);
    applyReindexRequired();
    notifyReindexRequired({ source: 'catalog-settings', ...detail });

    if (!runtime?.supports(ADMIN_RUNTIME_COMMANDS.INDEXING_MARK_REQUIRED)) {
      return Promise.resolve(null);
    }

    return sendRuntime(ADMIN_RUNTIME_COMMANDS.INDEXING_MARK_REQUIRED).then((resp) => {
      if (!cancelledRef.current && resp?.result === 'error') {
        setError(resp?.error || t('catalog.update.markRequiredFailed'));
      }
      return resp;
    }).catch((err) => {
      if (!cancelledRef.current) {
        setError(err?.message || t('catalog.update.markRequiredFailed'));
      }
      return null;
    });
  }, [applyReindexRequired, deferredReindexEnabled, runtime, sendRuntime, t]);

  const startFullUpdate = useCallback(() => {
    if (!enabled) return false;
    applyOptimisticIndexingRequest({ source: 'catalog-sync' });
    notifyIndexingRequested({ source: 'catalog-sync' });

    sendRuntime(ADMIN_RUNTIME_COMMANDS.INDEXING_START).then((resp) => {
      if (!cancelledRef.current && resp?.result === 'error') {
        const message = resp?.error || t('catalog.update.startFullFailed');
        applyIndexingRequestFailure({ source: 'catalog-sync', error: message });
        notifyIndexingRequestFailed({ source: 'catalog-sync', error: message });
      } else if (!cancelledRef.current && resp?.meta?.autokick_error) {
        setError(String(resp.meta.autokick_error));
      }
    }).catch((err) => {
      if (!cancelledRef.current) {
        const message = err?.message || t('catalog.update.startFullFailed');
        applyIndexingRequestFailure({ source: 'catalog-sync', error: message });
        notifyIndexingRequestFailed({ source: 'catalog-sync', error: message });
      }
    });
    return true;
  }, [applyOptimisticIndexingRequest, applyIndexingRequestFailure, enabled, sendRuntime, t]);

  const restartStaleQueuedJob = useCallback(() => {
    if (!enabled) return Promise.resolve(null);
    const payload = jobId ? { job_id: jobId } : {};
    setError(null);
    if (!runtime?.supports(ADMIN_RUNTIME_COMMANDS.INDEXING_RESTART_STALE)) {
      return Promise.resolve(null);
    }
    return sendRuntime(ADMIN_RUNTIME_COMMANDS.INDEXING_RESTART_STALE, payload).then((resp) => {
      if (!cancelledRef.current && resp?.result === 'error') {
        setError(resp?.error || t('catalog.update.restartStaleFailed'));
      } else if (!cancelledRef.current && resp?.meta?.autokick_error) {
        setError(String(resp.meta.autokick_error));
      }
      return resp;
    }).catch((err) => {
      if (!cancelledRef.current) {
        setError(err?.message || t('catalog.update.restartStaleFailed'));
      }
      return null;
    });
  }, [enabled, jobId, runtime, sendRuntime, t]);

  return {
    ...sourcePhase,
    status,
    rawStatus,
    progress,
    progressKnown,
    activeIndexPresent,
    error,
    isLoading,
    isDegraded,
    isStarting,
    lastSuccessAt,
    startedAt,
    finishedAt,
    jobId,
    queueAgeSeconds,
    isStaleQueue,
    restartAllowed,
    indexedCount,
    totalCount,
    reindexRequired,
    startFullUpdate,
    markReindexRequired,
    restartStaleQueuedJob,
  };
}
